import { useEffect, useMemo, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCreateMtoRunMutation, useListMtoRunsQuery } from '@shared/api';
import type { MtoRun } from '@entities/mto';
import { MTO_RECONCILE_DOCTYPES, apiErrorMessage, fmtDateTime, runRaw } from '@entities/mto';
import { useAgentPermission } from '@entities/user';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import { ACCEPT_ANY } from '@shared/lib/fileAccept';
import { Card, CardTitle } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Label, Select } from '@shared/ui/Input';
import { IconFile, IconTrash } from '@shared/ui/icons';

const Head = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Block = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const BlockLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`;

const Hint = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Chosen = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2.5)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};

  & strong {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const FileList = styled.ul`
  list-style: none;
  margin: ${({ theme }) => theme.spacing(3)} 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const FileItem = styled.li`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(2.5)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;

  & > svg {
    color: ${({ theme }) => theme.colors.text.muted};
    flex-shrink: 0;
  }
`;

const FileName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.status.danger};
    background: ${({ theme }) => theme.colors.surfaceHover};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin: ${({ theme }) => theme.spacing(2)} 0;
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

interface Attached {
  id: number;
  file: File;
  docType: 'request' | 'rd';
}

let seq = 0;

/** Подпись ведомости в выборе: объект, итог, дата. */
function vedomostLabel(run: MtoRun): string {
  const project = runRaw(run).project_name;
  return [project, fmtDateTime(run.created_at), run.summary?.trim()].filter(Boolean).join(' · ');
}

/**
 * Запуск сверки потребности с заявками (модуль 1.4). Потребность — готовая
 * ведомость материалов (по ссылке «Сверить с заявками» подставляется сама,
 * `?rd_run=<id>`; иначе человек выбирает её явно — подставлять «последнюю
 * чью-то» опасно: сверка пойдёт не с тем объектом). Заявки — файлы. Без
 * ведомости можно приложить РД файлом.
 */
export function MtoReconcileStart() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { can_write } = useAgentPermission('mto');
  const { activeProjectName, setActiveProject } = useActiveProjectContext();
  const urlRun = params.get('rd_run') ?? '';
  const [rdRunId, setRdRunId] = useState<string>(urlRun);
  const [docs, setDocs] = useState<Attached[]>([]);
  const [error, setError] = useState<string | null>(null);

  const vedQ = useListMtoRunsQuery({ module: 'rd_extract', scope: 'all', limit: 100 });
  const vedomosti = useMemo(
    () => (vedQ.data?.items ?? []).filter((r) => r.status === 'completed'),
    [vedQ.data],
  );
  const chosen = vedomosti.find((r) => r.id === rdRunId);
  const [createRun, { isLoading }] = useCreateMtoRunMutation();

  useEffect(() => {
    if (!chosen) return;
    const raw = runRaw(chosen);
    if (raw.project_id) {
      setActiveProject(raw.project_id, { name: raw.project_name ?? undefined });
    }
  }, [chosen, setActiveProject]);

  useEffect(() => {
    if (rdRunId || !activeProjectName || vedomosti.length === 0) return;
    const match = vedomosti.find(
      (r) => runRaw(r).project_name?.toLowerCase() === activeProjectName.toLowerCase(),
    );
    if (match) setRdRunId(match.id);
  }, [rdRunId, activeProjectName, vedomosti]);

  const requests = docs.filter((d) => d.docType === 'request').length;
  const rdFiles = docs.filter((d) => d.docType === 'rd').length;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (requests === 0) {
      setError('Приложите хотя бы одну заявку на закупку — без заявок сверять не с чем.');
      return;
    }
    if (!rdRunId && rdFiles === 0) {
      setError('Выберите ведомость материалов или приложите РД файлом.');
      return;
    }
    const fd = new FormData();
    fd.append('module', 'reconcile');
    if (rdRunId) fd.append('rd_run_id', rdRunId);
    for (const d of docs) fd.append(d.docType, d.file);
    try {
      const res = await createRun(fd).unwrap();
      setDocs([]);
      navigate(`/agents/mto/runs/${res.run_id}`);
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Не удалось запустить сверку. Попробуйте ещё раз.');
    }
  }

  return (
    <Card>
      <Head>
        <CardTitle>Новая сверка с заявками</CardTitle>
      </Head>
      <form onSubmit={submit}>
        <Block>
          <Label htmlFor="mto-rec-ved">Потребность — ведомость материалов</Label>
          <Select id="mto-rec-ved" value={rdRunId} onChange={(e) => setRdRunId(e.target.value)}>
            <option value="">— выберите ведомость (или приложите РД файлом ниже) —</option>
            {urlRun && !vedomosti.some((r) => r.id === urlRun) && (
              <option value={urlRun}>Ведомость из ссылки</option>
            )}
            {vedomosti.map((r) => (
              <option key={r.id} value={r.id}>
                {vedomostLabel(r)}
              </option>
            ))}
          </Select>
          {vedQ.isError && (
            <Err role="alert">
              Не удалось загрузить ведомости.{' '}
              <Button type="button" $variant="ghost" $size="sm" onClick={() => vedQ.refetch()}>
                Повторить
              </Button>
            </Err>
          )}
          {chosen && (
            <Chosen>
              <strong>{runRaw(chosen).project_name ?? 'Объект не указан'}</strong>
              <br />
              {chosen.summary}
              <br />
              сформирована {fmtDateTime(chosen.created_at)}
            </Chosen>
          )}
          <Hint>
            Из ведомости берутся материалы и свод арматуры: стержни — по своду, строки «на 1
            элемент» и убранные из ведомости — не берутся.
          </Hint>
        </Block>

        <Block>
          <BlockLabel id="mto-rec-files">Заявки на закупку</BlockLabel>
          {can_write ? (
            <FileDropZone
              multiple
              compact
              accept={ACCEPT_ANY}
              hint="Или нажмите, чтобы выбрать. Excel, PDF, скан, фото"
              onFiles={(added) => {
                setError(null);
                setDocs((prev) => [
                  ...prev,
                  ...added.map((file) => ({ id: ++seq, file, docType: 'request' as const })),
                ]);
              }}
            />
          ) : (
            <Muted>Только просмотр — загрузка недоступна.</Muted>
          )}
          {docs.length > 0 && (
            <FileList aria-labelledby="mto-rec-files">
              {docs.map((d) => (
                <FileItem key={d.id}>
                  <FileRow>
                    <IconFile size={15} />
                    <FileName title={d.file.name}>{d.file.name}</FileName>
                    <RemoveBtn
                      type="button"
                      aria-label={`Убрать файл ${d.file.name}`}
                      onClick={() => setDocs((prev) => prev.filter((x) => x.id !== d.id))}
                    >
                      <IconTrash size={16} />
                    </RemoveBtn>
                  </FileRow>
                  <Select
                    aria-label={`Что это за файл: ${d.file.name}`}
                    value={d.docType}
                    onChange={(e) =>
                      setDocs((prev) =>
                        prev.map((x) =>
                          x.id === d.id ? { ...x, docType: e.target.value as Attached['docType'] } : x,
                        ),
                      )
                    }
                  >
                    {MTO_RECONCILE_DOCTYPES.map((t) => (
                      <option key={t.docType} value={t.docType}>
                        {t.label}
                      </option>
                    ))}
                  </Select>
                </FileItem>
              ))}
            </FileList>
          )}
        </Block>

        {error && <Err role="alert">{error}</Err>}
        <Button
          type="submit"
          $variant="primary"
          disabled={!can_write || isLoading}
          title={!can_write ? 'Только просмотр' : undefined}
        >
          {isLoading ? 'Запуск…' : 'Сверить'}
        </Button>
      </form>
    </Card>
  );
}
